export default (entangled, suggestions) => ({
    open: false,
    // Flip the dropdown up when there is not enough room below the input.
    dropUp: false,
    // Keep the panel invisible until it has been positioned with its real
    // (rendered) height. Otherwise the first open paints with the 272px
    // fallback direction and then visibly jumps once $nextTick measures the
    // actual ~192px panel. Revealed in openDropdown()'s $nextTick.
    positioned: false,
    dropdownTop: 0,
    dropdownLeft: 0,
    dropdownWidth: 0,
    activeIndex: 0,
    value: entangled,
    elements: suggestions,

    handleScrollRef: null,
    handleResizeRef: null,
    handleClickRef: null,
    _boundPageHide: null,
    _morphedCleanup: null,
    _navigateRef: null,
    // Tracks whether the global scroll/resize/click listeners are currently
    // registered. Prevents double-registration if openDropdown() is called
    // repeatedly, and makes cleanup idempotent.
    _listenersActive: false,

    init() {
        this.handleClickRef = this.handleClick.bind(this);
        this.handleScrollRef = this.updateDropdownPosition.bind(this);
        this.handleResizeRef = this.updateDropdownPosition.bind(this);

        // bfcache / regular unload: close and drop global listeners so a
        // restored page does not come back with zombie handlers attached.
        this._boundPageHide = () => {
            this._forceClose();
        };
        window.addEventListener('pagehide', this._boundPageHide);

        // Livewire navigate (back/forward + bfcache): close the dropdown and
        // drop global listeners before the new page initialises. The dropdown
        // is teleported to <body>; purge it if it is about to survive as a
        // stale clone attached to a dead Alpine scope.
        this._navigateRef = () => {
            this._forceClose();
            const dd = this.$refs?.dropdown;
            if (dd && dd.isConnected && dd.parentNode === document.body) {
                try { dd.parentNode.removeChild(dd); } catch (e) {}
            }
        };
        window.addEventListener('livewire:navigate', this._navigateRef);
        window.addEventListener('livewire:navigating', this._navigateRef);

        // Livewire morph hook: if our root is gone or got morphed while the
        // dropdown is open, close it and drop the global listeners so we
        // don't leak scroll/resize/click handlers bound to detached DOM.
        if (typeof window !== 'undefined' && window.Livewire?.hook) {
            this._morphedCleanup = window.Livewire.hook('morphed', (component) => {
                if (!this.open) return;

                const root = this.$root;
                if (!root || !root.isConnected) {
                    this._forceClose();
                    return;
                }
                // Close only when the morph actually affects our own root –
                // either the root itself was morphed, or one of its ancestors
                // (which can detach/replace our DOM subtree). Morphs *inside*
                // our root are intentionally ignored: stale-ref guards in
                // updateDropdownPosition()/handleClick() catch edge cases.
                const el = component?.el;
                if (el && (el === root || el.contains(root))) {
                    this._forceClose();
                }
            });
        }

        this.$watch('value', () => {
            this.activeIndex = 0;
        });
    },
    get filteredElements() {
        // Alpine reconciliation can read this getter before initializing entangle's interceptor.
        return (typeof this.value !== 'string' || !this.value)
            ? this.elementObjects
            : this.elementObjects.filter((element, key) => {
                return element.name.toLowerCase().includes(this.value.toLowerCase())
            })
    },
    get elementObjects() {
        return Object.keys(this.elements)
            .map(key => ({
                id: key,
                name: this.elements[key],
            }));
    },
    get sortedFilteredElements() {
        // sort by element name
        return [...this.filteredElements].sort((elementA, elementB) => {
            return elementA.name.localeCompare(elementB.name);
        });
    },
    openDropdown() {
        this.positioned = false;
        this.open = true
        this.updateDropdownPosition();
        // updateDropdownPosition may have force-closed due to a stale/detached
        // container ref. In that case do not proceed to register listeners.
        if (!this.open) return;
        this.activeIndex = 0;

        // Reposition once the dropdown has actually rendered, so the up-flip can
        // use its measured height instead of the fallback estimate - then reveal
        // it (positioned) so the corrected direction is the first thing painted.
        if (typeof this.$nextTick === 'function') {
            this.$nextTick(() => {
                this.updateDropdownPosition();
                this.positioned = true;
            });
        } else {
            // No $nextTick (e.g. in unit tests): reveal immediately so the
            // panel is never stuck hidden.
            this.positioned = true;
        }

        this._registerEventListeners();
    },
    closeDropdown() {
        this.open = false
        this.positioned = false;
        this.activeIndex = null;

        this._cleanupEventListeners();
    },
    scrollToActiveElement() {
        // Guard: dropdown is teleported – after a morph it may be gone or
        // detached. Don't throw in that case, just skip scrolling.
        const dropdown = this.$refs.dropdown;
        if (!dropdown || !dropdown.isConnected) return;

        const activeItem = dropdown.querySelector(`[data-index='${this.activeIndex}']`);

        if (activeItem) {
            activeItem.scrollIntoView({
                behavior: 'smooth', // Optional: Adds smooth scrolling
                block: 'nearest',  // Ensures minimal scrolling
            });
        }
    },
    updateDropdownPosition() {
        if (!this.open) return;

        // Guard against stale refs. After a Livewire morph the container may
        // have been detached; calling getBoundingClientRect() on a missing
        // node would throw. In that case close the dropdown and drop the
        // global listeners instead of leaking them.
        const container = this.$refs.container;
        if (!container || !container.isConnected) {
            this._forceClose();
            return;
        }

        // Get input element dimensions and position
        const inputRect = container.getBoundingClientRect();

        // Flip up when there is not enough room below the input in the VIEWPORT
        // (not a scroll container) and there is more room above. Measure the
        // ACTUAL dropdown: x-ref="dropdown" is the (zero-height) wrapper, the
        // visible panel is its position:fixed first child - the wrapper's own
        // offsetHeight is 0 and would pin us to the fallback forever. Fall back
        // to 272px only before the panel has rendered on the very first open.
        const panel = this.$refs.dropdown?.firstElementChild;
        const dropdownHeight = panel?.offsetHeight || this.$refs.dropdown?.offsetHeight || 272;
        const spaceBelow = window.innerHeight - inputRect.bottom;
        this.dropUp = spaceBelow < dropdownHeight && inputRect.top > spaceBelow;

        // Try to detect if the dropdown is rendered within a modal container
        // You can either use document.getElementById or traverse up the ancestors
        const modalContainer = container.closest('#modal-container')
            || container.closest('.dialog');

        if (modalContainer) {
            // If inside a modal, get the modal's viewport position
            const modalRect = modalContainer.getBoundingClientRect();

            // Adjust the dropdown's position relative to the modal's coordinate space
            this.dropdownTop = this.dropUp
                ? inputRect.top - modalRect.top - dropdownHeight
                : inputRect.bottom - modalRect.top;
            this.dropdownLeft = inputRect.left - modalRect.left;
        } else {
            // If not inside the modal, stick with viewport coordinates
            this.dropdownTop = this.dropUp
                ? inputRect.top - dropdownHeight
                : inputRect.bottom;
            this.dropdownLeft = inputRect.left;
        }

        this.dropdownWidth = inputRect.width; // Match input width
    },
    selectActiveElement() {
        if (this.activeIndex === null) return;

        // Guard: teleported dropdown may be gone after morph.
        const dropdown = this.$refs.dropdown;
        if (!dropdown || !dropdown.isConnected) return;

        const activeItem = dropdown.querySelector(`[data-index='${this.activeIndex}']`);

        if (activeItem) {
            activeItem.click();
        }
    },
    setElement(element) {
        this.value = element;
        this.closeDropdown();
    },
    handleKeyDown(event) {
        if (!this.open) {
            this.openDropdown();
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.moveActiveIndex('down');
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.moveActiveIndex('up');
        } else if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            this.selectActiveElement();
        } else if (event.key === 'Tab') {
            this.closeDropdown();
        }
    },
    moveActiveIndex(direction) {
        if (!this.open) return;

        const maxIndex = this.sortedFilteredElements.length - 1;

        if (direction === 'up') {
            this.activeIndex = this.activeIndex <= 0 ? maxIndex : this.activeIndex - 1;
        } else if (direction === 'down') {
            this.activeIndex = this.activeIndex >= maxIndex ? 0 : this.activeIndex + 1;
        }

        this.scrollToActiveElement();
    },
    handleClick(event) {
        if (!this.open) return;

        // Check if the click was inside the container or the teleported dropdown.
        // Both refs can be missing/detached after a morph – guard with ?. to
        // avoid throwing on stale refs.
        const clickedInsideContainer = this.$refs.container?.contains(event.target);
        const clickedInsideDropdown = this.$refs.dropdown?.contains(event.target);

        // Don't close if clickt into container or if selection is clicked
        if (!clickedInsideContainer && !clickedInsideDropdown) {
            this.closeDropdown();
        }
    },
    _registerEventListeners() {
        if (this._listenersActive) return;
        window.addEventListener('scroll', this.handleScrollRef, true);
        window.addEventListener('resize', this.handleResizeRef);
        document.addEventListener('click', this.handleClickRef);
        this._listenersActive = true;
    },
    _cleanupEventListeners() {
        if (!this._listenersActive) return;
        window.removeEventListener('scroll', this.handleScrollRef, true);
        window.removeEventListener('resize', this.handleResizeRef);
        document.removeEventListener('click', this.handleClickRef);
        this._listenersActive = false;
    },
    // Close unconditionally and drop listeners. Used by morph / pagehide /
    // stale-ref paths where we cannot rely on this.open being accurate.
    _forceClose() {
        this.open = false;
        this.positioned = false;
        this.activeIndex = null;
        this._cleanupEventListeners();
    },
    destroy() {
        this._cleanupEventListeners();
        if (this._boundPageHide) {
            window.removeEventListener('pagehide', this._boundPageHide);
            this._boundPageHide = null;
        }
        if (typeof this._morphedCleanup === 'function') {
            this._morphedCleanup();
            this._morphedCleanup = null;
        }
        if (this._navigateRef) {
            window.removeEventListener('livewire:navigate', this._navigateRef);
            window.removeEventListener('livewire:navigating', this._navigateRef);
            this._navigateRef = null;
        }
    }
})
